import React, { useRef, useState } from "react";

import styled from "styled-components/macro";

import { useUserData } from "Flocknote/apiHooks";

import {
  useGetAdminList,
  useUpdateSignupListOption,
} from "Signups/SignupView/api/queries";
import { useSignups } from "Signups/state";

import FnrCheckNReveal from "UI/FnrCheckNReveal/FnrCheckNReveal";
import FnrLoadingBricks from "UI/FnrLoadingBricks";
import { FnrCombobox } from "UI/FnrSelectList3";
import FnrSimpleError from "UI/FnrSimpleError";

import { useGetCurrentSignupList } from "Utilities/Hooks/useGetCurrentSignupList";

/**
 * This component feels a little more complicated than it probably needs to be.
 * So I'm going to explain it in a little more detail.
 * The main point of complication is that we are saving the selected admins as each is clicked.
 * If they don't save properly, we need to revert to the default selection.
 * So we have an intermediate Selection and check if the selection has changed. (optimistically update)
 * I could probably wrap all the selection login in a custom hook, but we aren't going to use it anywhere else.
 * 
 * The other complication is that the data is in 3 formats:
 * 1. the adminView endpoint return the admin list as an array of objects
 * 2. the combobox needs an array of object, but with a different shape
 * 3. the selection needs to be an array of ids
 * 
 * So there is some converting between the formats, which makes it a little more complicated.
 */

export default function NotifyAdmin() {
  const [, dispatch] = useSignups();

  const { ID: signupID, notifyAdmins } = useGetCurrentSignupList();

  const [{ userID, isSuperDuper }] = useUserData();
  const defaultUserIDArray = isSuperDuper ? [] : [userID];

  const defaultSelection = useRef(
    notifyAdmins === undefined
      ? defaultUserIDArray
      : notifyAdmins.map((admin) => admin.userID)
  );
  const [intermediateSelection, setIntermediateSelection] = useState();
  const [hasChanged, setHasChanged] = useState(false);

  const [showOptions, setShowOptions] = useState(notifyAdmins?.length > 0);

  const { data, isError: adminListError } = useGetAdminList(signupID);

  const options =
    data?.admins?.map((admin) => {
      return {
        id: admin.userID,
        value: admin.userFullName,
        info: ` - ${admin.email}`,
      };
    }) || [];

  const handleSuccess = (res) => {
    dispatch({
      type: "Update Notify Admins",
      name: "notifyAdmins",
      value: data.admins.filter((admin) =>
        res?.options?.notifyAdmins.includes(admin.userID)
      ),
    });
    setHasChanged(true);
    setIntermediateSelection(undefined);
  };

  const handleError = (error) => {
    setIntermediateSelection(undefined);
  };

  const { mutate, isLoading, isError } = useUpdateSignupListOption({
    signupID,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const handleSelection = (value) => {
    setIntermediateSelection(value);
    mutate({ optionName: "notifyAdmins", value: value });
  };

  const handleCheckboxClick = () => {
    if (!showOptions) {
      // going from not checked to checked
      setShowOptions(true);
      handleSelection(defaultUserIDArray);
    } else {
      // going from checked to not checked
      setShowOptions(false);
      handleSelection([]);
    }
  };

  const getErrorMessage = () => {
    if (!showOptions) return null;

    if (adminListError) {
      return "We couldn't load the list of admins. If the problem continues, please contact help@flocknote.com.";
    }

    if (isError) {
      return "We had problems saving your changes. Please try again later. If the problem continues, please contact help@flocknote.com.";
    }

    return null;
  };

  const errorMessage = getErrorMessage();

  const getSelection = () => {
    if (intermediateSelection) {
      return intermediateSelection;
    }

    if (hasChanged) {
      if (notifyAdmins) return notifyAdmins.map((admin) => admin.userID);
      return [];
    }

    return defaultSelection.current;
  };

  return (
    <Wrapper>
      <CheckboxWrapper>
        <FnrCheckNReveal
          onClick={handleCheckboxClick}
          on={showOptions}
          revealedChildren={
            <ComboboxWrapper>
              <FnrCombobox
                options={options}
                selection={getSelection()}
                onSelect={handleSelection}
                multiselect={true}
                openIndicator={true}
                placeholder="Available Admins"
                controlled={true}
                style={{
                  fontSize: "14px",
                  fontFamily: '"Open Sans", sans-serif',
                  fontWeight: 400,
                  padding: "10px",
                  maxWidth: "300px",
                  textAlign: "left",
                  color: "#15436a",
                }}
              />
            </ComboboxWrapper>
          }
          style={{ marginBottom: 0 }}
        >
          Notify admins when someone signs up
        </FnrCheckNReveal>

        <FnrLoadingBricks show={isLoading} />
      </CheckboxWrapper>

      <FnrSimpleError
        show={Boolean(errorMessage)}
        style={{
          fontSize: ".875rem",
          marginLeft: "2.125rem",
          maxWidth: "494px",
        }}
      >
        {errorMessage}
      </FnrSimpleError>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-bottom: 20px;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ComboboxWrapper = styled.div`
  margin-left: 2.125rem;
`;